import axios, { InternalAxiosRequestConfig } from 'axios';
import React from 'react';

import SnackbarWithLinks from 'components/SnackbarWithLinks/SnackbarWithLinks';
import { appConfig } from 'constants/appConfig';
import {
  alphaSessionToken,
  expireAlphaPopupSession,
  inAlphaPopup,
} from 'services/alpha/alphaSession';
import { signOut } from 'utils/session';
import { enqueueSnackbar, SnackbarKey, SnackbarOrigin } from 'notistack';
import { LOGIN } from 'router/routes';

interface ISnackbarWithLinksProps {
  id: SnackbarKey;
  message: string | number | boolean | React.ReactElement | Iterable<React.ReactNode>;
  variant: 'error';
  persist: boolean;
  style: React.CSSProperties;
  anchorOrigin: SnackbarOrigin;
  hideIconVariant: boolean;
  iconVariant: Record<string, React.ReactNode>;
}

const axiosInstance = axios.create({
  baseURL: appConfig.baseUrl,
  withCredentials: false,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE',
    'Access-Control-Allow-Headers': 'Content-Type',
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const accessToken = alphaSessionToken();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  res => res,
  res => {
    if (res?.response?.status === 401 && res?.config?.url !== '/Token') {
      if (inAlphaPopup()) {
        expireAlphaPopupSession();
        return Promise.reject(res);
      }

      signOut();

      // Prefix LOGIN with the Vite base so the redirect stays inside the pod sub-path.
      // (process.env.PUBLIC_URL is undefined under Vite — use import.meta.env.BASE_URL.)
      const podBase = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
      window.location.replace(`${podBase}${LOGIN}`);

      return;
    }

    const errorConfig = res?.config as {
      skipErrorSnackbar?: boolean;
      rejectWithResponse?: boolean;
    } | undefined;

    if (errorConfig?.rejectWithResponse) {
      return Promise.reject(res);
    }

    // Allow individual requests to opt out of the global error snackbar by setting
    // `skipErrorSnackbar: true` on the request config. Used by features that surface
    // the error in their own UI surface (e.g. the CallScribe softphone, where the
    // tier-gating 503 is expected in dev environments without Twilio configured).
    if (errorConfig?.skipErrorSnackbar) {
      return;
    }

    const errorMessage = res?.response?.data?.message || 'An error occurred';

    enqueueSnackbar(errorMessage, {
      variant: 'error',
      autoHideDuration: 8000,
      content: (key, message) => {
        const props: ISnackbarWithLinksProps = {
          id: key,
          message: message ?? errorMessage,
          variant: 'error',
          persist: false,
          style: {},
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          hideIconVariant: false,
          iconVariant: {},
        };
        return React.createElement(SnackbarWithLinks, props);
      },
    });
  },
);

export default axiosInstance;
